import { useEffect, useMemo, useState } from "react";
import { LegalShell } from "@/components/legal/LegalShell";
import { api } from "@/lib/api";

const LAST_UPDATED = "September 14, 2026";

/**
 * Public review page for the Early Access Community Agreement. Renders
 * the SAME payload fetched from the backend (``GET /api/private-tester-
 * requests/agreement``) that the in-app modal uses, so this page is
 * always the source of truth. Bumping the server version updates both
 * places at once.
 *
 * The shared ``LegalShell`` expects a ``sections`` array where each
 * entry is ``{ id, title, body }`` (body is JSX) — we build that array
 * once the agreement payload arrives.
 */
export default function EarlyAccessAgreementPolicy() {
  const [agreement, setAgreement] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/private-tester-requests/agreement");
        setAgreement(data);
      } catch (e) {
        setErr("Unable to load the current agreement. Please try again shortly.");
      }
    })();
  }, []);

  const sections = useMemo(() => {
    if (!agreement) {
      return [
        {
          id: "loading",
          title: "Loading",
          body: (
            <p className="text-sm text-muted-foreground">
              {err ? err : "Loading the current agreement…"}
            </p>
          ),
        },
      ];
    }
    return [
      {
        id: "overview",
        title: "Overview",
        body: (
          <>
            <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground font-semibold">
              Version {agreement.version}
            </p>
            <p className="mt-3">{agreement.intro}</p>
          </>
        ),
      },
      {
        id: "your-commitments",
        title: "By continuing with Early Access, you agree",
        body: (
          <ul className="list-disc pl-6 space-y-2.5">
            {agreement.bullets.map((b, i) => {
              const dot = b.indexOf(".");
              const heading = dot > 0 ? b.slice(0, dot + 1) : "";
              const rest = dot > 0 ? b.slice(dot + 1).trimStart() : b;
              return (
                <li key={i} data-testid={`policy-early-access-bullet-${i}`}>
                  {heading && (
                    <strong className="font-semibold text-foreground">{heading}</strong>
                  )}
                  {heading ? " " : ""}
                  {rest}
                </li>
              );
            })}
          </ul>
        ),
      },
      ...(agreement.outro
        ? [{
            id: "moderation",
            title: "Moderation & enforcement",
            body: <p>{agreement.outro}</p>,
          }]
        : []),
      {
        id: "versioning",
        title: "About this document",
        body: (
          <p className="text-sm text-muted-foreground leading-relaxed">
            Early Access members are asked to accept this agreement before
            they can use private testing features. If we make material
            changes to these rules, the version number will be bumped and
            every existing member will be asked to re-accept.
          </p>
        ),
      },
    ];
  }, [agreement, err]);

  return (
    <LegalShell
      eyebrow="Community"
      title="Early Access Community Agreement"
      intro="The commitments every Early Access member accepts before using private testing features. This is the same agreement shown in-app; bumping the version re-prompts every existing member."
      lastUpdated={LAST_UPDATED}
      sections={sections}
      testid="policy-early-access-agreement"
    />
  );
}
